import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User } from '../types';
import { Search, ArrowUpDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { usePageTitle } from "../hooks/usePageTitle";
import RaccoonPrint from '../components/RaccoonPrint';
import GoogleSheetsApiService from '../services/googleSheetsApi';
import { getAvatarForUser, loadAvatarOverrides } from '../lib/wrapped/avatarUtils';
import {
  hasDaisySeal,
  normalizeLedgerUsername,
  resolveAvatarHonorStyle,
} from '../lib/daisySeal';
import { useAuthorizedUsernames } from '../hooks/useAuthorizedUsernames';
import { formatEasternLedgerTime } from '../lib/ledgerTime';
import { WingoLedgerAvatar } from '../components/WingoLedgerAvatar';
import { AuthorizedAvatarFrame } from '../components/AuthorizedAvatarFrame';
import { DaisySealBadge } from '../components/DaisySealBadge';

interface LedgerProps {
  user: User;
}

interface LeaderboardEntry {
  rank: number;
  user: string;
  balance: number;
  totalMined: number;
  distance: number;
  lastMined: string;
  votingShare: number;
  wingleL32?: number;
}

interface LogEntry {
  id: number;
  fullId: string;
  username: string;
  date: string;
  wingoMined: number;
  kmLogged: number;
  initiation: boolean;
  category: string;
  activityLink: string;
}

const Ledger: React.FC<LedgerProps> = ({ user }) => {
  // Data via React Query
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof LeaderboardEntry>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isLeaderboardView, setIsLeaderboardView] = useState(true);
  
  
  // Avatar modal state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<{username: string, avatarUrl: string, balance: number, totalMined: number, distance: number, rank: number} | null>(null);

  useEffect(() => {
    void loadAvatarOverrides();
  }, []);
  
  // Log data state
  // Queries
  const {
    data: leaderboardData = [],
    isLoading: isLeaderboardLoading,
    dataUpdatedAt: leaderboardUpdatedAt,
  } = useQuery({
    queryKey: ['leaderboard'],
    staleTime: 60000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<LeaderboardEntry[]> => {
      const sheetsService = GoogleSheetsApiService.getInstance();
      const data = await sheetsService.getLeaderboardData();
      return data;
    },
    select: (data: LeaderboardEntry[]) => {
      // Dedupe by normalized username
      const map = new Map<string, LeaderboardEntry>();
      for (const entry of data) {
        const key = entry.user.trim().toLowerCase();
        if (!map.has(key)) map.set(key, entry);
      }
      return Array.from(map.values());
    }
  });

  const { authorizedUsers } = useAuthorizedUsernames();

  const {
    data: logEntries = [],
    isLoading: isLogLoading,
    dataUpdatedAt: logUpdatedAt,
  } = useQuery({
    queryKey: ['logEntries'],
    staleTime: 60000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<LogEntry[]> => {
      const sheetsService = GoogleSheetsApiService.getInstance();
      const data = await sheetsService.getLogData();
      return data;
    },
    select: (data: LogEntry[]) => {
      // Dedupe log rows by a stable key.
      // The sheet's "ID" column may be blank/malformed for some rows, which would collapse many entries into `id=0`.
      const seen = new Set<string>();
      return data.filter((e) => {
        const fullId = e.fullId?.trim();
        const idPart = e.id && e.id !== 0 ? `id:${e.id}` : '';

        const key = fullId
          ? `fullId:${fullId}`
          : idPart
            ? idPart
            : `fallback:${e.username}|${e.date}|${e.category}|${e.activityLink}|${e.wingoMined}|${e.kmLogged}|${e.initiation}`;

        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
  });

  const lastUpdated = useMemo(() => {
    const ts = Math.max(leaderboardUpdatedAt ?? 0, logUpdatedAt ?? 0);
    if (!ts) return '';
    return formatEasternLedgerTime(new Date(ts));
  }, [leaderboardUpdatedAt, logUpdatedAt]);

  const isLoading = isLeaderboardLoading || isLogLoading;
  const [logSortField, setLogSortField] = useState<'date' | 'wingoMined' | 'km' | 'initiation' | 'username' | 'category' | 'fullId'>('date');
  const [logSortDirection, setLogSortDirection] = useState<'asc' | 'desc'>('desc');

  // Data fetching handled by React Query
  

  // Calculate total WINGO first
  const totalWingo = leaderboardData.reduce((sum, entry) => sum + entry.balance, 0);
  const totalMined = leaderboardData.reduce((sum, entry) => sum + entry.totalMined, 0);

  // Sort by balance and update ranks
  const sortedLeaderboard = [...leaderboardData]
    .sort((a, b) => b.balance - a.balance);

  // Calculate total kilometers as 0.32 * totalMined
  const totalKilometers = Number((totalMined * 0.32).toFixed(1));

  const handleSort = (field: keyof LeaderboardEntry) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort leaderboard data
  const filteredLeaderboard = sortedLeaderboard
    .filter(entry => {
      // Only show users who have activity in the log
      const hasLogActivity = logEntries.some(logEntry => 
        logEntry.username.toLowerCase() === entry.user.toLowerCase()
      );
      // Also apply search filter
      const matchesSearch = entry.user.toLowerCase().includes(searchTerm.toLowerCase());
      return hasLogActivity && matchesSearch;
    })
    .sort((a, b) => {
      let result = 0;
      if (sortField === 'rank') result = a.rank - b.rank;
      else if (sortField === 'user') result = a.user.localeCompare(b.user);
      else if (sortField === 'balance') result = b.balance - a.balance;
      else if (sortField === 'totalMined') result = b.totalMined - a.totalMined;
      else if (sortField === 'distance') result = b.distance - a.distance;
      else if (sortField === 'votingShare') result = b.votingShare - a.votingShare;
      else if (sortField === 'wingleL32') result = (b.wingleL32 ?? -Infinity) - (a.wingleL32 ?? -Infinity);
      else if (sortField === 'lastMined') result = new Date(b.lastMined).getTime() - new Date(a.lastMined).getTime();
      if (sortDirection === 'asc') result = -result;
      
      // Add stable secondary sort by username to prevent duplicates when primary values are equal
      if (result === 0) {
        result = a.user.localeCompare(b.user);
      }
      
      return result;
    });

  // Precompute users who have a Yellowstone log entry for special glow border
  const yellowstoneUsers = useMemo(
    () =>
      new Set(
        logEntries
          .filter((entry) => entry.category === 'Yellowstone')
          .map((entry) => normalizeLedgerUsername(entry.username)),
      ),
    [logEntries],
  );

  // fetchLogData removed (handled by React Query)

  // Ensure switching back to leaderboard clears any user filter
  const handleViewToggle = (nextIsLeaderboard: boolean) => {
    setIsLeaderboardView(nextIsLeaderboard);
    if (nextIsLeaderboard) {
      setSearchTerm('');
    }
  };

  const handleLogSort = (field: 'date' | 'wingoMined' | 'km' | 'initiation' | 'username' | 'category' | 'fullId') => {
    if (logSortField === field) {
      setLogSortDirection(logSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLogSortField(field);
      setLogSortDirection('desc');
    }
  };

  const getTopRecords = () => {
    // Consider only Mining entries, then sort by WINGO mined (desc), then by date (asc), then by ID (asc)
    const miningEntries = logEntries.filter((entry) => entry.category === 'Mining' && entry.wingoMined > 0);
    const sortedByWingo = [...miningEntries].sort((a, b) => {
      // First sort by WINGO mined (descending)
      if (b.wingoMined !== a.wingoMined) {
        return b.wingoMined - a.wingoMined;
      }
      
      // If WINGO mined is equal, sort by date (ascending - older dates first)
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      
      // If dates are equal, sort by ID (ascending - older IDs first)
      return a.id - b.id;
    });

    // Take top 3 and format them
    return sortedByWingo.slice(0, 3).map((entry, index) => ({
      rank: (index + 1).toString(),
      username: entry.username,
      date: entry.date,
      wingoMined: entry.wingoMined
    }));
  };

  // Get top 3 mining session IDs for medal emojis
  const getTopMiningSessionIds = () => {
    const miningEntries = logEntries.filter((entry) => entry.category === 'Mining' && entry.wingoMined > 0);
    const sortedByWingo = [...miningEntries].sort((a, b) => {
      if (b.wingoMined !== a.wingoMined) {
        return b.wingoMined - a.wingoMined;
      }
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.id - b.id;
    });
    
    return sortedByWingo.slice(0, 3).map(entry => entry.id);
  };

  // Get top OBWI Prize session ID for Winego indicator
  const getTopObwiPrizeSessionId = () => {
    const obwiEntries = logEntries.filter((entry) => entry.category === 'OBWI Prize' && entry.wingoMined > 0);
    if (obwiEntries.length === 0) return null;
    const sortedByWingo = [...obwiEntries].sort((a, b) => {
      if (b.wingoMined !== a.wingoMined) {
        return b.wingoMined - a.wingoMined;
      }
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.id - b.id;
    });
    return sortedByWingo[0].id;
  };

  // Get lowest mining session ID for poop emoji
  const getLowestMiningSessionId = () => {
    const miningEntries = logEntries.filter(entry => entry.wingoMined > 0);
    if (miningEntries.length === 0) return null;
    
    const sortedByWingo = [...miningEntries].sort((a, b) => {
      if (a.wingoMined !== b.wingoMined) {
        return a.wingoMined - b.wingoMined;
      }
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Newer dates first for ties
      }
      return b.id - a.id; // Newer IDs first for ties
    });
    
    return sortedByWingo[0].id;
  };

  // Get medal emoji for rank
  const getMedalEmoji = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  // Handle avatar click
  const handleAvatarClick = (entry: LeaderboardEntry) => {
    console.log('Avatar clicked for user:', entry.user);
    const avatarUrl = getAvatarForUser(entry.user);
    setSelectedAvatar({ 
      username: entry.user, 
      avatarUrl, 
      balance: entry.balance, 
      totalMined: entry.totalMined, 
      distance: entry.distance, 
      rank: entry.rank
    });
    setShowAvatarModal(true);
  };

  // Replace gearQuotes and getGearQuote with new lists and logic
  const gearList = [
    'Divot Awareness Pro',
    'LaceLoop Sync XT',
    'HeelLock 9™',
    'The Aglet Beacon',
    'TongueTamer Core',
    'LaceOS v4.7',
    'LoopPredictor Nano',
    'HitchAware Hybrid',
    'AIglet+ Cloud Lace',
    'ThreadLock Modulator',
    'Forefoot Lace Governor',
    'Autolace Recall Band',
    'Tempo Tug™ ProSeries',
    'The Obsessive Knot System (OKS)',
    'Carbon Weft Companion',
    'Midsole Lace Anchor Pro',
    'Instep Signal Syncer',
    'TensionSync 320',
    'GhostLace Obfuscator',
    'Pronation-Responsive Aglet Dock',
  ];
  const quoteList = [
    'Don’t trust a loop you didn’t tie.',
    'Pain is just uncompressed effort.',
    'You don’t win the WINGO. You earn her favor.',
    'Left foot for pace. Right foot for grace.',
    'Lace yourself. Before you face yourself.',
    'Never raced with a clean tongue.',
    'Each knot holds a memory.',
    'I stopped tying my shoes. I started listening to them.',
    'He brought flats to a PentaWingo.',
    'Wind from the east? DAISY’s teeth.',
    'My laces were taught by monks. Or maybe ghosts.',
    'I don’t train. I foreshadow.',
  ];
  function getGearQuote(username: string) {
    // Deterministic hash for consistent assignment
    let hash = 0;
    for (let i = 0; i < username.length; i++) hash += username.charCodeAt(i);
    const gear = gearList[hash % gearList.length];
    const quote = quoteList[hash % quoteList.length];
    return { gear, quote };
  }

  // Add state for Joni slider
  const [joniImageIndex, setJoniImageIndex] = useState(0);
  const joniImages = [
    '/avatars/joni.png',
    '/avatars/joni2.jpg',
    '/avatars/joni3.jpg',
    '/avatars/joni4.jpg',
  ];

  // Add state for Willy Wingo slider
  const [willyImageIndex, setWillyImageIndex] = useState(0);
  const willyImages = [
    '/avatars/willy_wingo.png',
    '/avatars/willy_wingo2.png',
    '/avatars/willy_wingo3.png',
  ];

  // Add state for Quark slider
  const [quarkImageIndex, setQuarkImageIndex] = useState(0);
  const quarkImages = [
    '/avatars/quark.png',
    '/avatars/quark2.png',
    '/avatars/quark3.png',
  ];

  // Touch state for Joni slider
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const x = e.touches[0].clientX;
    touchStartX.current = x;
    touchEndX.current = x;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null) return;
    if (touchEndX.current === null) touchEndX.current = touchStartX.current;
    const deltaX = touchEndX.current - touchStartX.current;
    console.log('handleTouchEnd deltaX:', deltaX);
    if (Math.abs(deltaX) > 10) { // lowered threshold for swipe
      if (deltaX < 0) {
        // Swipe left: next image (wrap around)
        setJoniImageIndex((prev) => (prev + 1) % joniImages.length);
      } else if (deltaX > 0) {
        // Swipe right: previous image (wrap around)
        setJoniImageIndex((prev) => (prev - 1 + joniImages.length) % joniImages.length);
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Touch state for Willy Wingo slider
  const willyTouchStartX = useRef<number | null>(null);
  const willyTouchEndX = useRef<number | null>(null);

  const handleWillyTouchStart = (e: React.TouchEvent) => {
    const x = e.touches[0].clientX;
    willyTouchStartX.current = x;
    willyTouchEndX.current = x;
  };

  const handleWillyTouchMove = (e: React.TouchEvent) => {
    willyTouchEndX.current = e.touches[0].clientX;
  };

  const handleWillyTouchEnd = () => {
    if (willyTouchStartX.current === null) return;
    if (willyTouchEndX.current === null) willyTouchEndX.current = willyTouchStartX.current;
    const deltaX = willyTouchEndX.current - willyTouchStartX.current;
    console.log('handleWillyTouchEnd deltaX:', deltaX);
    if (Math.abs(deltaX) > 10) { // lowered threshold for swipe
      if (deltaX < 0) {
        // Swipe left: next image (wrap around)
        setWillyImageIndex((prev) => (prev + 1) % willyImages.length);
      } else if (deltaX > 0) {
        // Swipe right: previous image (wrap around)
        setWillyImageIndex((prev) => (prev - 1 + willyImages.length) % willyImages.length);
      }
    }
    willyTouchStartX.current = null;
    willyTouchEndX.current = null;
  };

  // Touch state for Quark slider
  const quarkTouchStartX = useRef<number | null>(null);
  const quarkTouchEndX = useRef<number | null>(null);

  const handleQuarkTouchStart = (e: React.TouchEvent) => {
    const x = e.touches[0].clientX;
    quarkTouchStartX.current = x;
    quarkTouchEndX.current = x;
  };

  const handleQuarkTouchMove = (e: React.TouchEvent) => {
    quarkTouchEndX.current = e.touches[0].clientX;
  };

  const handleQuarkTouchEnd = () => {
    if (quarkTouchStartX.current === null) return;
    if (quarkTouchEndX.current === null) quarkTouchEndX.current = quarkTouchStartX.current;
    const deltaX = quarkTouchEndX.current - quarkTouchStartX.current;
    console.log('handleQuarkTouchEnd deltaX:', deltaX);
    if (Math.abs(deltaX) > 10) { // lowered threshold for swipe
      if (deltaX < 0) {
        // Swipe left: next image (wrap around)
        setQuarkImageIndex((prev) => (prev + 1) % quarkImages.length);
      } else if (deltaX > 0) {
        // Swipe right: previous image (wrap around)
        setQuarkImageIndex((prev) => (prev - 1 + quarkImages.length) % quarkImages.length);
      }
    }
    quarkTouchStartX.current = null;
    quarkTouchEndX.current = null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-12">
        {/* Page Title */}
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            <span className="inline-flex items-baseline">
              <span className="text-[#E6C200] font-bold">W</span>
              <span>INGO</span>
            </span> Ledger
          </h1>
          <a 
            href="/raq" 
            className="text-xs text-wingo-600 hover:text-wingo-500 italic -mt-1 mb-2 block"
          >
            WTF is WINGO?
          </a>
        </div>

        {/* Stats Overview */}
        <div className="bg-gradient-to-br from-white via-green-50 to-green-200 rounded-full border-[10px] border-red-500/70 px-8 py-4 mb-6 max-w-fit mx-auto shadow-[0_0_70px_rgba(34,197,94,0.4)] hover:shadow-[0_0_80px_rgba(34,197,94,0.5)] shadow-inner-[0_0_20px_rgba(34,197,94,0.2)] transition-all duration-300 relative">
          <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 w-[0.55px] h-2.5 bg-white z-10"></div>
          <div className="absolute -bottom-1.5 left-1/3 transform translate-x-2 -rotate-30 w-1.5 h-0.5 bg-black z-10 opacity-30 rounded-sm"></div>
          <div className="flex flex-col items-center justify-center text-center">
            <span className="text-gray-800 text-xl sm:text-3xl font-bold tracking-wide">
              {totalMined.toLocaleString()} <span className="text-[#E6C200] drop-shadow-sm">W</span> Mined
            </span>
            <div className="flex items-center gap-1 mt-1">
              <img src="/raccoon_icon.png" alt="Raccoon" className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-gray-500 font-medium text-lg sm:text-2xl">{totalKilometers} km</span>
            </div>
          </div>
        </div>

        {/* Wingate Invitational Alert - removed */}

        {/* Leaderboard */}
        {isLeaderboardView && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 h-12 sm:h-auto border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white sticky top-0 z-20">
              <div className="grid grid-cols-3 items-center sm:flex sm:items-center sm:justify-between sm:gap-4 relative">
                {/* Search left */}
                <div className="hidden sm:flex sm:flex-1">
                  <div className="relative w-full max-w-[160px]">
                    <input
                      type="text"
                      placeholder=""
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-transparent"
                    />
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                </div>
                {/* Toggle center - grid center on mobile, flex center on desktop */}
                <div className="col-start-2 col-span-1 flex flex-col justify-center items-center min-w-0 text-center ml-3 sm:ml-0">
                  {/* Toggle Button */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 mb-0 sm:mb-1 h-8 sm:h-9 flex items-center">
                    <div className="flex">
                      <button
                        onClick={() => handleViewToggle(true)}
                        className={`px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                          isLeaderboardView
                            ? 'bg-[#E6C200] text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span className="hidden sm:inline">WINGO Leaderboard</span>
                        <span className="sm:hidden">Rank</span>
                      </button>
                      <button
                        onClick={() => handleViewToggle(false)}
                        className={`px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                          !isLeaderboardView
                            ? 'bg-[#E6C200] text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span className="hidden sm:inline">Ransaction Log</span>
                        <span className="sm:hidden">Log</span>
                      </button>
                    </div>
                  </div>
                </div>
                {/* DAISY badge right (static size/position) */}
                <div className="col-start-3 col-span-1 flex items-center justify-end sm:flex-1 absolute right-0 top-1 sm:static">
                  <span className="inline-block px-2.5 py-0.5 sm:px-3 sm:py-1 bg-gray-900 text-white rounded-md text-[13px] sm:text-sm font-medium">
                    D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
                  </span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 font-mono">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th 
                        scope="col" 
                        className="pl-1 sm:pl-6 pr-1 sm:pr-4 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('rank')}
                      >
                        Rank
                      </th>
                      <th 
                        scope="col" 
                        className="w-8 sm:w-10 py-3 text-center text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        
                      </th>
                      <th 
                        scope="col" 
                        className="pl-2 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('user')}
                      >
                        Runner
                      </th>
                      <th 
                        scope="col" 
                        className="px-0 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('balance')}
                      >
                        <div className="flex flex-col sm:block">
                          <span className="hidden sm:inline">WINGO Balance</span>
                          <span className="sm:hidden">WINGO</span>
                          <span className="sm:hidden">Balance</span>
                        </div>
                      </th>
                      <th 
                        scope="col" 
                        className="px-1 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 hidden sm:table-cell"
                        onClick={() => handleSort('votingShare')}
                      >
                        <div className="flex flex-col sm:block">
                          <span className="hidden sm:inline">Voting Share</span>
                          <span className="sm:hidden">Vote</span>
                          <span className="sm:hidden">Share</span>
                        </div>
                      </th>
                      <th 
                        scope="col" 
                        className="px-0 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('totalMined')}
                      >
                        <div className="flex flex-col sm:block">
                          <span className="hidden sm:inline">WINGO mined</span>
                          <span className="sm:hidden">WINGO</span>
                          <span className="sm:hidden">mined</span>
                        </div>
                      </th>
                      <th 
                        scope="col" 
                        className="pl-1 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('wingleL32')}
                      >
                        {/* Mobile: two rows, nudged up slightly */}
                        <div className="flex flex-col leading-tight sm:hidden -mt-0.5">
                          <span className="font-medium">L32</span>
                        </div>
                        {/* Desktop: one line */}
                        <span className="hidden sm:inline">L32</span>
                      </th>
                      <th 
                        scope="col" 
                        className="px-1 sm:px-6 py-3 text-left text-[9px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastMined')}
                      >
                        <div className="flex flex-col sm:block">
                          <span className="hidden sm:inline">Last Mined</span>
                          <span className="sm:hidden">Last</span>
                          <span className="sm:hidden">Mined</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredLeaderboard.length > 0 ? (
                      filteredLeaderboard.map((entry: LeaderboardEntry, index: number) => {
                        const normalizedUser = normalizeLedgerUsername(entry.user);
                        const honorStyle = resolveAvatarHonorStyle(
                          normalizedUser,
                          authorizedUsers,
                          yellowstoneUsers,
                        );
                        return (
                          <tr key={`${entry.user}-${entry.rank}`} className={`hover:bg-gray-50 transition-colors border-b border-gray-50 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                            <td className="pl-1 sm:pl-6 pr-0 sm:pr-4 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900">{entry.rank}</td>
                            <td className="w-8 sm:w-10 py-4 flex justify-center">
                              <WingoLedgerAvatar
                                username={entry.user}
                                instanceId={entry.rank}
                                honorStyle={honorStyle}
                                onClick={() => handleAvatarClick(entry)}
                              />
                            </td>
                            <td className="pl-2 sm:px-6 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900">
                              <span 
                                className="cursor-pointer hover:text-[#E6C200] transition-colors"
                                onClick={() => handleAvatarClick(entry)}
                              >
                                {entry.user}
                              </span>
                            </td>
                            <td className="px-0 sm:px-6 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900">
                              <div className="flex flex-col sm:block">
                                <span className="sm:hidden font-bold">{entry.balance}</span>
                                <span className="text-[7px] sm:text-xs text-gray-500 sm:hidden">{entry.votingShare.toFixed(1)}%</span>
                                <span className="hidden sm:inline">{entry.balance}</span>
                              </div>
                            </td>
                            <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900 hidden sm:table-cell">{entry.votingShare.toFixed(1)}%</td>
                            <td className="px-0 sm:px-6 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900 text-left">
                              <div className="flex flex-col sm:block">
                                <span className="sm:hidden">{entry.totalMined}</span>
                                <span className="text-[7px] sm:text-xs text-gray-500 sm:hidden">{entry.distance.toFixed(1)}km</span>
                                <span className="hidden sm:inline">{entry.totalMined} <span className="text-[6px] sm:text-xs text-gray-500">({entry.distance.toFixed(1)}km)</span></span>
                              </div>
                            </td>
                            <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[9px] sm:text-sm text-gray-900">
                              {entry.wingleL32 !== undefined ? entry.wingleL32 : '--'}
                            </td>
                            <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[7px] sm:text-sm text-gray-900">
                              {entry.lastMined}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={8} className="px-4 sm:px-6 py-4 text-center text-gray-500">
                          {isLoading ? 'Loading...' : (searchTerm ? 'No matching users found' : 'No Wingo activity yet')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        
        {/* Log View */}
        {!isLeaderboardView && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 sm:px-6 py-3 h-12 sm:h-auto border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white sticky top-0 z-20">
              <div className="grid grid-cols-3 items-center sm:flex sm:items-center sm:justify-between sm:gap-4 relative">
                {/* Mobile dropdown left (only on Log) */}
                <div className={`sm:hidden col-start-1 col-span-1 min-w-0 -mt-1 -ml-1 ${isLeaderboardView ? 'invisible' : 'visible'}`}> 
                  <div className="relative w-full max-w-[130px] min-w-[110px]">
                    {(() => {
                      const userOptions = Array.from(new Set(logEntries.map(e => e.username))).sort((a, b) => a.localeCompare(b));
                      return (
                        <>
                          <select
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full h-8 pr-7 pl-3 py-1 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-[#E6C200] bg-gray-50 text-[12px] leading-tight text-gray-800 appearance-none"
                          >
                            <option value="">All users</option>
                            {userOptions.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {/* Search left (desktop) */}
                <div className="flex-1 min-w-0 hidden sm:block">
                  <div className="relative w-full max-w-[180px]">
                    {(() => {
                      const userOptions = Array.from(new Set(logEntries.map(e => e.username))).sort((a, b) => a.localeCompare(b));
                      return (
                        <select
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full h-8 pr-6 pl-2 py-1 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E6C200] focus:border-[#E6C200] bg-gray-50 text-[12px] leading-tight text-gray-800"
                        >
                          <option value="">All users</option>
                          {userOptions.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                </div>
                {/* Toggle center - grid center on mobile, flex center on desktop; always centered */}
                <div className="col-start-2 col-span-1 basis-[30%] sm:flex-1 flex justify-center items-center min-w-0 px-1 sm:px-0 mt-0 sm:mt-0 ml-3 sm:ml-0">
                  {/* Toggle Button */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1 mb-1 h-8 sm:h-9 flex items-center">
                    <div className="flex">
                      <button
                        onClick={() => handleViewToggle(true)}
                        className={`px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                          isLeaderboardView
                            ? 'bg-[#E6C200] text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span className="hidden sm:inline">WINGO Leaderboard</span>
                        <span className="sm:hidden">Rank</span>
                      </button>
                      <button
                        onClick={() => handleViewToggle(false)}
                        className={`px-3 py-1 sm:py-1.5 text-xs font-medium rounded-md transition-colors ${
                          !isLeaderboardView
                            ? 'bg-[#E6C200] text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <span className="hidden sm:inline">Ransaction Log</span>
                        <span className="sm:hidden">Log</span>
                      </button>
                    </div>
                  </div>
                </div>
                {/* DAISY badge right (static size/position) */}
                <div className="col-start-3 col-span-1 flex items-center justify-end sm:flex-1 absolute right-0 top-1 sm:static">
                  <span className="inline-block px-2.5 py-0.5 sm:px-3 sm:py-1 bg-gray-900 text-white rounded-md text-[13px] sm:text-sm font-medium">
                    D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
                  </span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 font-mono">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleLogSort('date')}
                    >
                      Date
                      {logSortField === 'date' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLogSort('username')}
                    >
                      Runner
                      {logSortField === 'username' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleLogSort('wingoMined')}
                    >
                      <span className="hidden sm:inline">Δ WINGO</span>
                      <span className="sm:hidden">Δ WINGO</span>
                      {logSortField === 'wingoMined' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => handleLogSort('km')}
                    >
                      <span className="hidden sm:inline">KM Logged</span>
                      <span className="sm:hidden">KM</span>
                      {logSortField === 'km' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLogSort('category')}
                    >
                      Tag
                      {logSortField === 'category' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                    <th 
                      className="px-1 sm:px-6 py-3 text-left text-[8px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleLogSort('fullId')}
                    >
                      W-ID
                      {logSortField === 'fullId' && (
                        <span className="ml-1">
                          {logSortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logEntries
                    .filter(entry => 
                      searchTerm === '' || 
                      entry.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      entry.fullId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      entry.category.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .sort((a, b) => {
                      if (logSortField === 'date') {
                        const dateA = new Date(a.date);
                        const dateB = new Date(b.date);
                        const dateComparison = logSortDirection === 'asc' 
                          ? dateA.getTime() - dateB.getTime()
                          : dateB.getTime() - dateA.getTime();
                        
                        if (dateComparison === 0) {
                          return logSortDirection === 'asc'
                            ? a.id - b.id
                            : b.id - a.id;
                        }
                        return dateComparison;
                      } else if (logSortField === 'wingoMined') {
                        return logSortDirection === 'asc'
                          ? a.wingoMined - b.wingoMined
                          : b.wingoMined - a.wingoMined;
                      } else if (logSortField === 'km') {
                        return logSortDirection === 'asc'
                          ? a.kmLogged - b.kmLogged
                          : b.kmLogged - a.kmLogged;
                      } else if (logSortField === 'username') {
                        return logSortDirection === 'asc'
                          ? a.username.localeCompare(b.username)
                          : b.username.localeCompare(a.username);
                      } else if (logSortField === 'category') {
                        return logSortDirection === 'asc'
                          ? a.category.localeCompare(b.category)
                          : b.category.localeCompare(a.category);
                      } else if (logSortField === 'fullId') {
                        return logSortDirection === 'asc'
                          ? a.fullId.localeCompare(b.fullId)
                          : b.fullId.localeCompare(a.fullId);
                      } else {
                        return logSortDirection === 'asc'
                          ? (a.initiation === b.initiation ? 0 : a.initiation ? 1 : -1)
                          : (a.initiation === b.initiation ? 0 : a.initiation ? -1 : 1);
                      }
                    })
                    .map((entry) => {
                      const topMiningSessionIds = getTopMiningSessionIds();
                      const lowestMiningSessionId = getLowestMiningSessionId();
                      const topObwiPrizeSessionId = getTopObwiPrizeSessionId();
                      const isTopMiningSession = topMiningSessionIds.includes(entry.id);
                      const isLowestMiningSession = entry.id === lowestMiningSessionId;
                      const topRank = isTopMiningSession ? topMiningSessionIds.indexOf(entry.id) + 1 : 0;
                      
                      return (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[7px] sm:text-sm text-gray-500">
                            {format(new Date(entry.date), 'M.dd.yy')}
                          </td>
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[8px] sm:text-sm text-gray-900">{entry.username}</td>
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[8px] sm:text-sm text-gray-900">
                            {entry.wingoMined > 0 ? '+' : ''}{entry.wingoMined}
                            {isTopMiningSession && (
                              <span className="ml-1">{getMedalEmoji(topRank)}</span>
                            )}
                            {entry.id === topObwiPrizeSessionId && entry.category === 'OBWI Prize' && (
                              <a href="/obwi-results" className="ml-1 text-wingo-600 hover:text-wingo-700 hover:underline">🍷</a>
                            )}
                            {(isLowestMiningSession || entry.wingoMined === 1) && (
                              <span className="ml-1">🪙</span>
                            )}
                            {entry.category === 'Dancing Daisy' && (
                              <span className="ml-1">🐎</span>
                            )}
                            {entry.category === 'Wager' && entry.wingoMined > 0 && (
                              <span className="ml-1">🤑</span>
                            )}
                            {entry.initiation && (
                              <span className="ml-1 text-[#E6C200]">🚀</span>
                            )}
                          </td>
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[8px] sm:text-sm text-gray-500">
                            {entry.category === 'Mining' ? entry.kmLogged.toFixed(2) : '--'}
                          </td>
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[8px] sm:text-sm text-gray-500">
                            {entry.category === 'Gate Unlock' ? (
                              <a 
                                href="/wingate-invitational" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-wingo-600 hover:text-wingo-700 hover:underline cursor-pointer"
                              >
                            {entry.category}
                              </a>
                            ) : entry.category === 'Dancing Daisy' ? (
                              <a 
                                href="https://www.daisy320.com/game" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-wingo-600 hover:text-wingo-700 hover:underline cursor-pointer"
                              >
                                {entry.category}
                              </a>
                            ) : entry.category === 'OBWI Prize' ? (
                              <a 
                                href="/obwi-results" 
                                className="text-wingo-600 hover:text-wingo-700 hover:underline cursor-pointer"
                              >
                                {entry.category}
                              </a>
                            ) : (
                              entry.category
                            )}
                          </td>
                          <td className="px-1 sm:px-6 py-4 whitespace-nowrap text-[8px] sm:text-sm text-gray-500">
                            {entry.fullId}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Text Coach DAISY™ Link */}
        <div className="mt-8 text-center">
          <p className="text-[13px] sm:text-lg text-gray-600">
            Text Coach DAISY™ at{' '}
            <a href="sms:9299254744" className="text-wingo-600 hover:text-wingo-700 font-medium">
              929-WAK-GRIG
            </a>{' '}
            to start mining
          </p>
          <p className="text-[10px] sm:text-xs text-gray-500 italic sm:mt-1">
            {lastUpdated && (
              <>Updated: {lastUpdated} 🐎🤖🪽8️⃣</>
            )}
          </p>
          <p className="text-xs text-gray-500 mt-2 px-2 py-1 bg-gray-50 border border-gray-200 rounded inline-block mb-2 sm:mb-0">
            Brought to you by{' '}
            <a 
              href="https://wingo320.com/old-balance" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-wingo-600 hover:text-wingo-700 underline"
            >
              Old Balance
            </a>
          </p>
        </div>

        {/* Avatar Modal */}
        {showAvatarModal && selectedAvatar && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-sm perspective-1000">
              <div className="relative w-full h-96 transform-style-preserve-3d transition-transform duration-700" id="avatarCard">
                {/* Front Side - Keep exactly as is */}
                <div className="absolute inset-0 w-full h-full backface-hidden">
                  <AuthorizedAvatarFrame
                    sealed={hasDaisySeal(selectedAvatar.username, authorizedUsers, yellowstoneUsers)}
                    className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden"
                  >
                    <div className="relative">
                      {selectedAvatar.username.trim().toLowerCase() === 'joni' ? (
                        <div
                          className="relative w-full h-96 flex items-center justify-center select-none"
                          onTouchStart={handleTouchStart}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                        >
                          {/* Left/Right tap overlays */}
                          <div
                            className="absolute left-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Previous image"
                            onClick={() => setJoniImageIndex((prev) => (prev - 1 + joniImages.length) % joniImages.length)}
                          />
                          <div
                            className="absolute right-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Next image"
                            onClick={() => setJoniImageIndex((prev) => (prev + 1) % joniImages.length)}
                          />
                          <img
                            src={joniImages[joniImageIndex]}
                            alt="Joni avatar"
                            className="w-full h-96 object-cover rounded-xl"
                            style={{ objectPosition: joniImages[joniImageIndex].includes('joni') ? 'center 40%' : 'center 25%' }}
                            onError={e => { (e.target as HTMLImageElement).src = '/avatars/joni.png'; }}
                            onTouchStart={e => { console.log('Joni image touch start', e.touches[0].clientX); handleTouchStart(e); }}
                            onTouchMove={e => { console.log('Joni image touch move', e.touches[0].clientX); handleTouchMove(e); }}
                            onTouchEnd={e => { console.log('Joni image touch end'); handleTouchEnd(); }}
                          />
                          {/* Dot indicators, clickable on mobile */}
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {joniImages.map((_, idx) => (
                              <button
                                key={idx}
                                className={`inline-block w-2 h-2 rounded-full focus:outline-none 
                                  ${joniImageIndex === idx 
                                    ? 'bg-yellow-400 sm:bg-[#E6C200] border-none'
                                    : 'bg-gray-300 border border-white sm:bg-gray-300 sm:border-none'}
                                `}
                                aria-label={`Go to image ${idx + 1}`}
                                onClick={() => { console.log('Dot clicked', idx); setJoniImageIndex(idx); }}
                                onTouchStart={() => { console.log('Dot touch start', idx); }}
                                onTouchEnd={() => { console.log('Dot touch end', idx); }}
                                tabIndex={0}
                                style={{ pointerEvents: 'auto' }}
                                disabled={joniImageIndex === idx}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (() => {
                        console.log('Checking username:', selectedAvatar.username, 'normalized:', selectedAvatar.username.trim().toLowerCase());
                        const normalizedUsername = selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_');
                        console.log('Normalized username:', normalizedUsername);
                        return normalizedUsername === 'willy_wingo';
                      })() ? (
                        <div
                          className="relative w-full h-96 flex items-center justify-center select-none"
                          onTouchStart={handleWillyTouchStart}
                          onTouchMove={handleWillyTouchMove}
                          onTouchEnd={handleWillyTouchEnd}
                        >
                          {/* Left/Right tap overlays */}
                          <div
                            className="absolute left-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Previous image"
                            onClick={() => setWillyImageIndex((prev) => (prev - 1 + willyImages.length) % willyImages.length)}
                          />
                          <div
                            className="absolute right-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Next image"
                            onClick={() => setWillyImageIndex((prev) => (prev + 1) % willyImages.length)}
                          />
                          <img
                            src={willyImages[willyImageIndex]}
                            alt="Willy Wingo avatar"
                            className="w-full h-96 object-cover rounded-xl"
                            style={{ objectPosition: 'center 25%' }}
                            onLoad={() => console.log('Willy image loaded successfully:', willyImages[willyImageIndex])}
                            onError={e => { 
                              console.log('Willy image failed to load:', willyImages[willyImageIndex]);
                              (e.target as HTMLImageElement).src = '/avatars/willy_wingo.png'; 
                            }}
                            onTouchStart={e => { console.log('Willy image touch start', e.touches[0].clientX); handleWillyTouchStart(e); }}
                            onTouchMove={e => { console.log('Willy image touch move', e.touches[0].clientX); handleWillyTouchMove(e); }}
                            onTouchEnd={e => { console.log('Willy image touch end'); handleWillyTouchEnd(); }}
                          />
                          {/* Dot indicators, clickable on mobile */}
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {willyImages.map((_, idx) => (
                              <button
                                key={idx}
                                className={`inline-block w-2 h-2 rounded-full focus:outline-none 
                                  ${willyImageIndex === idx 
                                    ? 'bg-yellow-400 sm:bg-[#E6C200] border-none'
                                    : 'bg-gray-300 border border-white sm:bg-gray-300 sm:border-none'}
                                `}
                                aria-label={`Go to image ${idx + 1}`}
                                onClick={() => { console.log('Dot clicked', idx); setWillyImageIndex(idx); }}
                                onTouchStart={() => { console.log('Dot touch start', idx); }}
                                onTouchEnd={() => { console.log('Dot touch end', idx); }}
                                tabIndex={0}
                                style={{ pointerEvents: 'auto' }}
                                disabled={willyImageIndex === idx}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (() => {
                        console.log('Checking username:', selectedAvatar.username, 'normalized:', selectedAvatar.username.trim().toLowerCase());
                        const normalizedUsername = selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '');
                        console.log('Normalized username:', normalizedUsername);
                        return normalizedUsername === 'quark';
                      })() ? (
                        <div
                          className="relative w-full h-96 flex items-center justify-center select-none"
                          onTouchStart={handleQuarkTouchStart}
                          onTouchMove={handleQuarkTouchMove}
                          onTouchEnd={handleQuarkTouchEnd}
                        >
                          {/* Left/Right tap overlays */}
                          <div
                            className="absolute left-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Previous image"
                            onClick={() => setQuarkImageIndex((prev) => (prev - 1 + quarkImages.length) % quarkImages.length)}
                          />
                          <div
                            className="absolute right-0 top-0 w-1/2 h-full z-0 cursor-pointer"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                            aria-label="Next image"
                            onClick={() => setQuarkImageIndex((prev) => (prev + 1) % quarkImages.length)}
                          />
                          <img
                            src={quarkImages[quarkImageIndex]}
                            alt="Quark avatar"
                            className="w-full h-96 object-cover rounded-xl"
                            style={{ objectPosition: quarkImages[quarkImageIndex].includes('quark3') ? 'center 20%' : 'center 10%' }}
                            onLoad={() => console.log('Quark image loaded successfully:', quarkImages[quarkImageIndex])}
                            onError={e => { 
                              console.log('Quark image failed to load:', quarkImages[quarkImageIndex]);
                              (e.target as HTMLImageElement).src = '/avatars/quark.png'; 
                            }}
                            onTouchStart={e => { console.log('Quark image touch start', e.touches[0].clientX); handleQuarkTouchStart(e); }}
                            onTouchMove={e => { console.log('Quark image touch move', e.touches[0].clientX); handleQuarkTouchMove(e); }}
                            onTouchEnd={e => { console.log('Quark image touch end'); handleQuarkTouchEnd(); }}
                          />
                          {/* Dot indicators, clickable on mobile */}
                          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                            {quarkImages.map((_, idx) => (
                              <button
                                key={idx}
                                className={`inline-block w-2 h-2 rounded-full focus:outline-none 
                                  ${quarkImageIndex === idx 
                                    ? 'bg-yellow-400 sm:bg-[#E6C200] border-none'
                                    : 'bg-gray-300 border border-white sm:bg-gray-300 sm:border-none'}
                                `}
                                aria-label={`Go to image ${idx + 1}`}
                                onClick={() => { console.log('Dot clicked', idx); setQuarkImageIndex(idx); }}
                                onTouchStart={() => { console.log('Dot touch start', idx); }}
                                onTouchEnd={() => { console.log('Dot touch end', idx); }}
                                tabIndex={0}
                                style={{ pointerEvents: 'auto' }}
                                disabled={quarkImageIndex === idx}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (
                        <img
                          src={selectedAvatar.avatarUrl}
                          alt={`${selectedAvatar.username}'s avatar`}
                          className="w-full h-96 object-cover"
                          style={{
                            objectPosition:
                              selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'pr_tek'
                                ? 'center 10%'
                                : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'mapster'
                                ? 'center 10%'
                                : selectedAvatar.username === 'Coach_2'
                                ? 'center 20%'
                                : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'trimbawolf'
                                ? 'center 10%'
                                : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'borneo2run'
                                ? 'center 10%'
                                : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'barry'
                                ? 'center 2%'
                                : 'center 25%'
                          }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src.includes('/avatars/default.png')) return;
                            target.src = '/avatars/default.png';
                          }}
                        />
                      )}
                      {/* Username Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                          {selectedAvatar.username}
                        </h3>
                      </div>
                      {hasDaisySeal(selectedAvatar.username, authorizedUsers, yellowstoneUsers) && (
                        <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 z-[15] pointer-events-none">
                          <DaisySealBadge size={40} />
                        </div>
                      )}
                      {/* Close Button */}
                      <button
                        onClick={() => setShowAvatarModal(false)}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl font-bold bg-black/30 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur-sm z-20"
                      >
                        <span className="-mt-0.5">×</span>
                      </button>
                    </div>
                    {/* Stats Section */}
                    <div className="p-2">
                      <div className="bg-gray-50 rounded-lg p-2 flex flex-row items-center justify-start w-full">
                        {/* WINGO Balance with Rank */}
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-[#E6C200] to-[#FFD700] rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">W</span>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 font-medium">Balance</div>
                            <div className="text-lg font-bold text-gray-900">
                              {/* Mobile: balance · #rank */}
                              <span className="sm:hidden flex items-center">
                                {selectedAvatar.balance.toLocaleString()}
                                <span className="mx-1 text-gray-400">&middot;</span>
                                <span className="text-sm font-medium text-gray-600 align-middle">#{leaderboardData.find(e => e.user === selectedAvatar?.username)?.rank ?? '--'}</span>
                              </span>
                              {/* Desktop: balance (Rank: X) */}
                              <span className="hidden sm:inline">
                                {selectedAvatar.balance.toLocaleString()}
                                <span className="text-xs text-gray-500 font-normal ml-1">(Rank: {leaderboardData.find(e => e.user === selectedAvatar?.username)?.rank ?? '--'})</span>
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Voting Share */}
                        <div className="text-left ml-6">
                          <div className="text-xs text-gray-500 font-medium">Voting Share</div>
                          <div className="text-lg font-bold text-gray-900">
                            {leaderboardData.find(e => e.user === selectedAvatar?.username)?.votingShare.toFixed(1) ?? '--'}%
                          </div>
                        </div>
                        {/* Mined Stats */}
                        <div className="text-left ml-6">
                          <div className="text-xs text-gray-500 font-medium">Mined</div>
                          <div className="text-lg font-bold text-gray-900">
                            {selectedAvatar.totalMined.toLocaleString()} <span className="text-xs text-gray-500 font-normal">
                              {/* Mobile: custom decimal logic */}
                              <span className="sm:hidden">
                                ({selectedAvatar.distance >= 10
                                  ? Math.round(selectedAvatar.distance)
                                  : selectedAvatar.distance.toFixed(1)
                                } km)
                              </span>
                              {/* Desktop: always one decimal */}
                              <span className="hidden sm:inline">
                                ({selectedAvatar.distance.toFixed(1)} km)
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AuthorizedAvatarFrame>
                </div>
                {/* Back Side - Baseball Card Style (removed, see ledger-back-card-backup.tsx for backup) */}
                {/*
                <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180">
                  ... (see backup file)
                </div>
                */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ledger; 